<template>
  <div class="geogrids-widget-panel scroll ver">
    <div class="geogrids-widget-panel__box geogrids-widget-panel__head" :class="{ 'border-bottom-0': geogridDataCollapsed }">
      <img v-if="logoPath" :src="logoPath" alt="" class="logo">

      <div class="text-primary">
        <a v-if="businessMapUrl" :href="businessMapUrl" target="_blank" class="geogrids-widget-panel__business-name d-flex align-items-start gap-2">
          <span>
            {{ geogrid.attrs.businessName }}
          </span>
          <img :src="require('arrow-up-right-from-square.svg')" alt="">
        </a>
        <span v-else class="geogrids-widget-panel__business-name">{{ geogrid.attrs.businessName }}</span>
        <button
          v-if="competitorIndex !== -1"
          type="button"
          class="btn btn-primary btn-xs gap-2 mt-2"
          @click="selectCompetitor(competitorIndex)">
          <i class="fa-solid fa-chevron-left" />
          Back to my geogrid
        </button>
        <div class="d-flex align-items-center flex-wrap gap-1 text-dark d-md-none">
          <span class="text-sm text-muted">Search Term:</span>
          <p class="m-0 text-sm font-weight-bold">
            {{ geogrid.searchTerm }}
          </p>
        </div>
      </div>

      <span v-if="geogrid.attrs.businessStoreCode && !geogridDataCollapsed" class="d-none d-md-block">{{ geogrid.attrs.businessStoreCode }}</span>

      <p v-if="geogrid.attrs.businessAddress && !geogridDataCollapsed" class="geogrids-widget-panel__address m-0 d-none d-md-block">
        {{ geogrid.attrs.businessAddress }}
      </p>
    </div>

    <div v-if="!geogridDataCollapsed" class="geogrids-widget-panel__box geogrids-widget-panel__box--gap-1">
      <span class="text-sm text-muted">Search Term:</span>
      <p class="geogrids-widget-panel__search-term m-0 font-weight-bold">
        {{ geogrid.searchTerm }}
      </p>
    </div>

    <div v-if="!geogridDataCollapsed" class="geogrids-widget-panel__box border-bottom-0">
      <div class="geogrid-info">
        <span class="text-sm text-muted">Google region:</span>
        <div class="data">
          <span v-if="geogrid.attrs.localLanguageEnabled && geogrid.attrs.businessCountry">{{ geogrid.attrs.businessCountry }}</span>
          <span v-else>Global</span>
        </div>
      </div>
      <div class="geogrids-widget-panel__line">
        <div class="geogrid-info">
          <span class="text-sm text-muted">Created:</span>
          <div class="text-sm data">{{ formatDate }}</div>
        </div>
        <div class="geogrid-info">
          <span class="text-sm text-muted">Grid:</span>
          <div class="data">{{ geogrid.attrs.gridFormatted }}</div>
        </div>
      </div>
      <div class="geogrids-widget-panel__metrics">
        <div v-for="{ name, value } in metrics" :key="name" class="geogrids-widget-panel__metric">
          <div class="text-sm text-muted">{{ name }}:</div>
          <div class="data">{{ value }}</div>
        </div>
        <div>
          <i class="far fa-info-circle text-primary" id="ranks-explanation" />
          <b-popover
            placement="top"
            boundary="window"
            target="ranks-explanation"
            custom-class="ranks-explanation-popover"
            triggers="hover">
            <div class="ranks-explanation">
              <div>
                <b class="text-sm">AGR</b>
                <p class="text-muted text-xs m-0">
                  Average Grid Rank. Only nodes with ranks equal to "20" and less considered.
                </p>
              </div>
              <div>
                <b class="text-sm">ATGR</b>
                <p class="text-muted text-xs m-0">
                  Average Total Grid Rank. All nodes considered. The "21" value is used for nodes with ranks over "20".
                </p>
              </div>
              <div>
                <b class="text-sm">SoLV</b>
                <p class="text-muted text-xs m-0">
                  Share of Local Voice indicates the percentage of search nodes where your business ranks "3" or better.
                  A higher value, close to 100%, signifies a stronger market presence.
                </p>
              </div>
            </div>
          </b-popover>
        </div>
      </div>
    </div>

    <div class="geogrids-widget-panel__box geogrids-widget-panel__box--collapse-btn pt-0">
      <i class="fa-solid fa-chevron-up" :class="{ 'fa-rotate-180': geogridDataCollapsed }" @click="toogleGeogridData" />
    </div>

    <div v-if="geogrid.competitors?.length" class="geogrids-widget-panel__box geogrids-widget-panel__box--competitors scroll ver">
      <strong class="d-flex justify-content-between text-sm text-muted px-4 pt-3 pb-2">
        <span>Competitors:</span>
        <span>{{ geogrid.competitors.length }}</span>
      </strong>
      <GeogridCompetitor
        v-for="(competitor, index) in geogrid.competitors"
        :key="index"
        :competitor="competitor"
        :class="['geogrids-widget-panel__competitor', { selected: index === competitorIndex }]"
        @click.native="selectCompetitor(index)">
        <template #rank>
          {{ index + 1 }}
        </template>
      </GeogridCompetitor>
    </div>
  </div>
</template>

<script>
import { BPopover } from 'bootstrap-vue'
import GeogridCompetitor from '../../components/geogrid_competitor'
import formatISODatetime from '../../../helpers/formatters'

export default {
  components: { BPopover, GeogridCompetitor },
  props: {
    geogrid: { type: Object, required: true },
    logoPath: { type: String, default: '' }
  },
  created() {
    this.formatDate = formatISODatetime(this.geogrid.createdAt, 'fullWithTZ')
  },
  data() {
    return {
      geogridDataCollapsed: false,
      competitorIndex: -1
    }
  },
  computed: {
    businessMapUrl() {
      if (!this.geogrid.attrs.businessPlaceId) return ''

      return `https://www.google.com/maps/place/?q=place_id:${this.geogrid.attrs.businessPlaceId}`
    },
    metrics() {
      const NA = 'N/A'
      const round = (num) => Math.round(num * 100) / 100
      const format = (num) => (parseFloat(num) ? round(num) : NA)
      const fixValue = (value) => {
        const formattedValue = format(value)
        return formattedValue !== NA ? formattedValue.toFixed(2) : formattedValue
      }
      return [
        {
          name: 'AGR',
          value: fixValue(this.geogrid.agr)
        },
        {
          name: 'ATGR',
          value: fixValue(this.geogrid.atgr)
        },
        {
          name: 'SoLV',
          value: this.geogrid.solv ? `${(round(this.geogrid.solv) * 100).toFixed(0)}%` : '0%'
        }
      ]
    }
  },
  methods: {
    toogleGeogridData() {
      this.geogridDataCollapsed = !this.geogridDataCollapsed
    },
    selectCompetitor(competitorIndex) {
      if (this.competitorIndex === competitorIndex) {
        this.competitorIndex = -1
      } else {
        this.competitorIndex = competitorIndex
      }
      this.$emit('selectCompetitor', this.competitorIndex)
    }
  }
}
</script>
